import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseAxios from 'src/utils/baseAxios';

export const GetPhoneChannel = createAsyncThunk('/GetPhoneChannel', async () => {
    const response = await baseAxios.get(`/OmniChannel/GetPhoneChannel`);
    return response.data;
})

export const AddPhoneLog = createAsyncThunk('/AddPhoneLog', async (phoneLogDto) => {
    const response = await baseAxios.post(`/OmniChannel/AddPhoneLog`, phoneLogDto);
    return response.data;
})

export const UpdatePhoneLog = createAsyncThunk('/UpdatePhoneLog', async (phoneLogId) => {
    const response = await baseAxios.post(`/OmniChannel/UpdatePhoneLog`, {
        phoneLogId: phoneLogId
    });
    return response.data;
})

export const GetConsumerByPhoneNumber = createAsyncThunk('/GetConsumerByPhoneNumber', async (phoneNumber) => {
    const response = await baseAxios.post(`/OmniChannel/GetConsumerByPhoneNumber`, {
        phoneNumber: phoneNumber
    });
    return response.data;
})

export const GetOutboundCallActivitiesList = createAsyncThunk(
    '/GetOutboundCallActivitiesList',
    async ({ regionId, serviceShopId, surveyStatus, startDate, endDate, pageNumber = 1, pageSize = 20 }, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams([
                ['RegionId', regionId],
                ['ServiceShopId', serviceShopId],
                ['SurveyStatus', surveyStatus || ''],
                ['StartDate', startDate],
                ['EndDate', endDate],
                ['pageNumber', pageNumber],
                ['pageSize', pageSize]
            ]);
            const response = await baseAxios.get(`/OmniChannel/GetOutboundCallActivitiesList`, { params });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const UpdateOutboundCall = createAsyncThunk(
    '/UpdateOutboundCall',
    async (dto, { rejectWithValue }) => {
        try {
            const response = await baseAxios.post(`/OmniChannel/UpdateOutboundCall`, dto);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);
export const CreateOutboundPhoneCall = createAsyncThunk('/CreateOutboundPhoneCall', async ({ consumerId, phoneNumber }) => {
    const response = await baseAxios.post(`/OmniChannel/CreateOutboundPhoneCall`, null, {
        params: { consumerId, phoneNumber }
    });
    return response.data;
})

const communicationSlice = createSlice({
    name: 'communication',
    initialState: {
        loading: 'Idle',
        error: null,
        phoneChannel: null,
        addPhoneLogResponse: null,
        updatePhoneLogResponse: null,
        consumerResponse: null,
        incomingPhoneNumber: null,
        currentPhoneLogId: null,
        activeCustomer: null, // Yeni eklenen alan
        outboundCallActivitiesResult: null,
        outboundCallActivitiesLoading: false,
        outboundCallActivitiesError: null,
        updateOutboundCallLoading: false,
        updateOutboundCallError: null,
    },
    reducers: {
        // Add synchronous reducers here as needed
        setIncomingPhoneNumber: (state, action) => {
            state.incomingPhoneNumber = action.payload;
        },
        clearIncomingPhoneNumber: (state) => {
            state.incomingPhoneNumber = null;
        },
        setCurrentPhoneLogId: (state, action) => {
            state.currentPhoneLogId = action.payload;
        },
        clearCurrentPhoneLogId: (state) => {
            state.currentPhoneLogId = null;
        },
        setActiveCustomer: (state, action) => {
            state.activeCustomer = action.payload;
        },
        clearActiveCustomer: (state) => {
            state.activeCustomer = null;
        }
    },
    extraReducers: {
        [GetPhoneChannel.pending]: (state) => {
            state.loading = 'Loading';
        },
        [GetPhoneChannel.rejected]: (state, action) => {
            state.error = action.payload;
            state.loading = 'Idle';
        },
        [GetPhoneChannel.fulfilled]: (state, action) => {
            state.phoneChannel = action.payload;
            state.loading = 'Finished';
        },
        [AddPhoneLog.pending]: (state) => {
            state.loading = 'Loading';
        },
        [AddPhoneLog.rejected]: (state, action) => {
            state.error = action.payload;
            state.loading = 'Idle';
        },
        [AddPhoneLog.fulfilled]: (state, action) => {
            state.addPhoneLogResponse = action.payload;

            // API returns plain int (entity.Id) — handle both int and object shapes
            const logId = typeof action.payload === 'number'
                ? action.payload
                : (action.payload?.id ||
                   action.payload?.Id ||
                   action.payload?.logId ||
                   action.payload?.LogId ||
                   action.payload?.phoneLogId ||
                   action.payload?.PhoneLogId);

            if (logId) {
                state.currentPhoneLogId = logId;
            }

            state.loading = 'Finished';
        },
        [UpdatePhoneLog.pending]: (state) => {
            state.loading = 'Loading';
        },
        [UpdatePhoneLog.rejected]: (state, action) => {
            state.error = action.payload;
            state.loading = 'Idle';
        },
        [UpdatePhoneLog.fulfilled]: (state, action) => {
            state.updatePhoneLogResponse = action.payload;
            state.loading = 'Finished';
        },
        [GetConsumerByPhoneNumber.pending]: (state) => {
            state.loading = 'Loading';
        },
        [GetConsumerByPhoneNumber.rejected]: (state, action) => {
            state.error = action.payload;
            state.loading = 'Idle';
        },
        [GetConsumerByPhoneNumber.fulfilled]: (state, action) => {
            state.consumerResponse = action.payload;
            state.loading = 'Finished';
        },
        [GetOutboundCallActivitiesList.pending]: (state) => {
            state.outboundCallActivitiesLoading = true;
            state.outboundCallActivitiesError = null;
        },
        [GetOutboundCallActivitiesList.rejected]: (state, action) => {
            state.outboundCallActivitiesError = action.payload;
            state.outboundCallActivitiesLoading = false;
        },
        [GetOutboundCallActivitiesList.fulfilled]: (state, action) => {
            state.outboundCallActivitiesResult = action.payload ?? null;
            state.outboundCallActivitiesLoading = false;
        },
        [UpdateOutboundCall.pending]: (state) => {
            state.updateOutboundCallLoading = true;
            state.updateOutboundCallError = null;
        },
        [UpdateOutboundCall.rejected]: (state, action) => {
            state.updateOutboundCallError = action.payload;
            state.updateOutboundCallLoading = false;
        },
        [UpdateOutboundCall.fulfilled]: (state) => {
            state.updateOutboundCallLoading = false;
        }
    }
});

export const communicationActions = {
    ...communicationSlice.actions,
};
export const communicationThunks = {
    GetPhoneChannel,
    AddPhoneLog,
    UpdatePhoneLog,
    GetConsumerByPhoneNumber,
    GetOutboundCallActivitiesList,
    UpdateOutboundCall
}

export default communicationSlice.reducer;
